import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, BadgePercent, Info } from "lucide-react";
import { toast } from "sonner";

import type { CommercialParameters } from "@/api/commercialCalibration";
import { CompactHero } from "@/components/brand/CompactHero";
import { Button } from "@/components/ui/button";
import {
  BaseMissingNotice,
  BaseSummary,
} from "@/components/projects/commercial-calibration/BaseSummary";
import { IncentivesCard } from "@/components/projects/commercial-calibration/IncentivesCard";
import { ParametersCard } from "@/components/projects/commercial-calibration/ParametersCard";
import { ScenarioCard } from "@/components/projects/commercial-calibration/ScenarioCard";
import {
  filledForm,
  initialForm,
  type CommercialForm,
} from "@/components/projects/commercial-calibration/form";
import { useProject } from "@/hooks/useApi";
import {
  useCommercialCalibration,
  useCommercialCalibrationPreview,
  useSaveCommercialCalibration,
} from "@/hooks/useCommercialCalibration";
import { detailCode } from "@/lib/api-error";
import { formatDateTime } from "@/lib/datetime";

/** Espera antes de recalcular, para digitar num campo não disparar uma chamada por tecla. */
const DEBOUNCE_MS = 250;

/**
 * Calibragem comercial (EF-FUNC-012).
 *
 * Onde Vendas transforma o custo da Calibragem técnica em preço: até três
 * cenários com margem alvo e fundings, e o que vai ao Diagnóstico. Só `sales` e
 * `admin` chegam aqui; o catálogo de rotas (`lib/project-nav.ts`) e o servidor
 * barram os outros.
 *
 * ## A conta é do servidor
 *
 * A tela não espelha o motor: cada mudança vai à prévia (`POST .../preview`), que
 * roda o mesmo motor da gravação, sobre a mesma base, sem gravar. O motor
 * arredonda em decimal exato, e um espelho em float erraria um centavo nos casos
 * de meio centavo. O que a tela mostra é o que salvar grava.
 */
export default function ProjectCommercialCalibration() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();
  const { data: project } = useProject(projectId);
  const { data, isPending, refetch } = useCommercialCalibration(projectId);
  const preview = useCommercialCalibrationPreview(projectId);
  const save = useSaveCommercialCalibration(projectId);

  //: Uma vez só, quando a calibragem chega: depois disso o estado é da pessoa,
  //: e reaplicar o ponto de partida apagaria o que ela mexeu.
  const [form, setForm] = useState<CommercialForm | null>(null);
  const scenarioName = useCallback(
    (position: number) => t("Scenario {{position}}", { position }),
    [t],
  );
  useEffect(() => {
    if (form || !data) return;
    setForm(initialForm(data, scenarioName));
  }, [form, data, scenarioName]);
  //: A versão com que a tela foi recarregada depois que outra pessoa salvou no meio.
  const [reloadedWith, setReloadedWith] = useState<number | null>(null);

  const changeParameter = useCallback((field: keyof CommercialParameters, value: number | null) => {
    setForm((current) =>
      current ? { ...current, parameters: { ...current.parameters, [field]: value } } : current,
    );
  }, []);

  const changeScenario = useCallback(
    (index: number, patch: Partial<CommercialForm["scenarios"][number]>) => {
      setForm((current) =>
        current
          ? {
              ...current,
              scenarios: current.scenarios.map((scenario, i) =>
                i === index ? { ...scenario, ...patch } : scenario,
              ),
            }
          : current,
      );
    },
    [],
  );

  //: Só um cenário vai ao Diagnóstico (v0.3 §6): marcar um desmarca os outros, e
  //: clicar no marcado o desmarca, porque nenhum marcado também é permitido.
  const toggleDiagnostic = useCallback((index: number) => {
    setForm((current) =>
      current
        ? {
            ...current,
            scenarios: current.scenarios.map((scenario, i) => ({
              ...scenario,
              take_to_diagnostic: i === index ? !scenario.take_to_diagnostic : false,
            })),
          }
        : current,
    );
  }, []);

  const hasBase = !!data && data.current_base.missing.length === 0;
  //: O nome é obrigatório no contrato: um cenário sem nome devolveria o 422 do
  //: corpo, e a tela trocaria a conta por um erro genérico. Ela espera o nome.
  const named = !!form && form.scenarios.every((scenario) => scenario.name.trim());
  //: Campo numérico vazio é erro no campo, e não 0: a prévia e a gravação esperam.
  const filled = form ? filledForm(form) : null;
  const { mutate: recalculate } = preview;
  //: Para QUAL formulário a prévia foi calculada, como na Calibragem técnica.
  //: Entre a última tecla e a resposta, a prévia na tela é a ANTERIOR, e salvar
  //: nessa janela gravaria números que a pessoa não viu.
  const [computedFor, setComputedFor] = useState<CommercialForm | null>(null);
  useEffect(() => {
    if (!form || !filled || !hasBase || !named) return;
    const timer = setTimeout(
      () => recalculate(filled, { onSuccess: () => setComputedFor(form) }),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [form, filled, hasBase, named, recalculate]);

  const latest = data?.latest;
  const stale = computedFor !== form;
  //: Os erros da EF (v0.1 RF-06, v0.3 §6) vêm da prévia, do mesmo motor da gravação.
  const blocked = (!!form && !filled) || (!stale && (preview.data?.issues.length ?? 0) > 0);
  const noneSelected = !!form && !form.scenarios.some((scenario) => scenario.take_to_diagnostic);
  const canSave =
    hasBase && named && !!filled && !stale && !blocked && !preview.isError && !save.isPending;
  //: Com parâmetro vazio nenhuma conta vale; com campo vazio num cenário, só a
  //: dele. Fica em traço, como o campo fora da faixa que a prévia devolve sem linhas.
  const parametersFilled =
    !!form && Object.values(form.parameters).every((value) => value !== null);
  const scenarioFilled = (index: number) =>
    !!form && filledForm({ parameters: form.parameters, scenarios: [form.scenarios[index]] }) !== null;

  const submit = () => {
    const bomVersion = data?.current_base.bom_version;
    const technicalVersion = data?.current_base.technical_calibration_version;
    if (!filled || bomVersion == null || technicalVersion == null) return;
    save.mutate(
      {
        ...filled,
        based_on_version: latest?.version ?? null,
        bom_version: bomVersion,
        technical_calibration_version: technicalVersion,
      },
      {
        onSuccess: (saved) => {
          setReloadedWith(null);
          toast.success(t("Version {{version}} saved.", { version: saved.version }));
        },
        //: Outra pessoa salvou uma versão enquanto esta tela editava. Manter o que
        //: estava digitado e salvar de novo gravaria por cima dela sem mostrar o
        //: que ela fez: a tela recarrega com a versão nova, e a pessoa refaz.
        //: A base mudada (BOM ou calibragem técnica) não recarrega: as escolhas
        //: continuam valendo, e a prévia refaz a conta.
        onError: async (error) => {
          if (detailCode(error) !== "version_changed_since_edit") return;
          const fresh = (await refetch()).data;
          if (!fresh) return;
          setForm(initialForm(fresh, scenarioName));
          setReloadedWith(fresh.latest?.version ?? null);
        },
      },
    );
  };
  const saveErrorCode = save.error ? detailCode(save.error) : null;
  const saveError =
    save.error && saveErrorCode !== "version_changed_since_edit"
      ? saveErrorMessage(saveErrorCode, t)
      : null;

  return (
    <div className="flex flex-col gap-6">
      <CompactHero
        glyph={<BadgePercent className="h-5 w-5" />}
        overline={project?.name ?? ""}
        title={t("Commercial calibration")}
        meta={
          isPending
            ? t("Loading the commercial calibration…")
            : latest
              ? t("Version {{version}} · saved on {{when}} by {{who}}", {
                  version: latest.version,
                  when: formatDateTime(latest.saved_at),
                  who: latest.saved_by_name ?? "—",
                })
              : t("No version saved yet")
        }
        actions={
          hasBase ? (
            <Button onClick={submit} disabled={!canSave}>
              {t("Save commercial calibration")}
            </Button>
          ) : null
        }
      />

      {data && !hasBase ? <BaseMissingNotice missing={data.current_base.missing} /> : null}

      {data && hasBase && form ? (
        <>
          {data.base_changed && latest ? (
            <p
              data-testid="commercial-base-changed"
              className="flex items-start gap-2 rounded-md border border-[#e7d3a3] bg-[#fdf8ec] px-3 py-2 text-sm text-[#7a5600]"
            >
              <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[#b57a00]" />
              {t(
                "The TCV, the hours or the average cost changed after version {{version}} was saved. Review the scenarios and save again.",
                { version: latest.version },
              )}
            </p>
          ) : null}

          {reloadedWith != null ? (
            <p
              data-testid="commercial-reloaded"
              className="flex items-start gap-2 rounded-md border border-[#e7d3a3] bg-[#fdf8ec] px-3 py-2 text-sm text-[#7a5600]"
            >
              <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[#b57a00]" />
              {t(
                "Another version (v{{version}}) was saved while you were editing. The screen was reloaded with it: redo your changes and save again.",
                { version: reloadedWith },
              )}
            </p>
          ) : null}
          {saveError ? (
            <p
              data-testid="commercial-save-error"
              className="rounded-md border border-[#e8c4c4] bg-[#fdf4f4] p-3 text-sm text-[#8a3a3a]"
            >
              {saveError}
            </p>
          ) : null}
          {blocked ? (
            <p
              data-testid="commercial-save-blocked"
              className="rounded-md border border-[#e8c4c4] bg-[#fdf4f4] p-3 text-sm text-[#8a3a3a]"
            >
              {t("Fix the marked fields to save.")}
            </p>
          ) : null}
          {noneSelected ? (
            <p
              data-testid="commercial-none-selected"
              className="flex items-start gap-2 rounded-md border border-[#e7d3a3] bg-[#fdf8ec] px-3 py-2 text-sm text-[#7a5600]"
            >
              <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-[#b57a00]" />
              {t(
                "No scenario is taken to the Diagnostic. You can save, and the Investment shows “Awaiting commercial calibration”.",
              )}
            </p>
          ) : null}

          {/* MOC v3: um card só, com a base, os parâmetros, os incentivos e os cenários. */}
          <section
            data-testid="commercial-calibration-card"
            className="flex flex-col gap-4 rounded-xl border border-[#e4e9f1] bg-white p-5 shadow-sm"
          >
            <div>
              <h2 className="text-[15px] font-extrabold text-[#17233a]">
                {t("Commercial calibration — value origin")}
              </h2>
              <p className="mt-1 text-sm text-[#5a6b86]">
                {t(
                  "Build up to three scenarios with target margin and fundings, and mark which one goes to the Diagnostic.",
                )}
              </p>
            </div>

            <BaseSummary base={data.current_base} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ParametersCard
                parameters={form.parameters}
                issues={preview.data?.issues}
                onChange={changeParameter}
              />
              <IncentivesCard
                softwareTcv={data.current_base.software_tcv}
                commissionPct={form.parameters.commission_pct}
                incentives={parametersFilled ? preview.data?.incentives : null}
              />
            </div>

            {preview.isError ? (
              <p
                data-testid="commercial-preview-error"
                className="rounded-md border border-[#e8c4c4] bg-[#fdf4f4] p-3 text-sm text-[#8a3a3a]"
              >
                {t("The scenarios could not be recalculated.")}
              </p>
            ) : null}

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {form.scenarios.map((scenario, index) => (
                <ScenarioCard
                  key={index}
                  position={index + 1}
                  scenario={scenario}
                  result={scenarioFilled(index) ? preview.data?.results[index] : null}
                  issues={preview.data?.issues}
                  onChange={(patch) => changeScenario(index, patch)}
                  onToggleDiagnostic={() => toggleDiagnostic(index)}
                />
              ))}
            </div>
            <p className="font-mono text-[10.5px] text-[#9aa6bb]">
              {t(
                "Project total = cost ÷ (1 − taxes) ÷ (1 − target margin) · Exed incentive = C&S funding + A&O funding · Hyperscale funding (separate) · Offered value = project total − Exed incentive − Hyperscale funding",
              )}
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}

/**
 * A frase da recusa da gravação, pelo código.
 *
 * Base mudada não pede nada além de conferir: o hook relê a calibragem, e a prévia
 * refaz a conta sobre a base nova com o que a pessoa digitou. Versão mudada não
 * passa por aqui: a tela recarrega com ela (ver `submit`). Base que deixou de
 * existir aparece no aviso de base faltando, que toma o lugar da tela.
 */
function saveErrorMessage(code: string | null, t: (key: string) => string): string {
  if (code === "base_changed_since_edit") {
    return t(
      "The BOM or the technical calibration changed while you were editing. The numbers were updated: check the scenarios and save again.",
    );
  }
  if (code === "invalid_commercial_calibration") return t("Fix the marked fields to save.");
  return t("Could not save the commercial calibration.");
}
